import InfoTooltip from 'gcs-common/components/InfoTooltip/InfoTooltip';
import { CHIP_TOOLTIP_TEXT } from 'gcs-common/constants/ChipConstants';
import { CustomerResponsibilityMemberTypes } from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersConstants';
import {
  getCustomerResponsibilitiesMembers,
  getCustomerResponsibilitiesMembersPaginationResult,
  getCustomerResponsibilitiesMembersStateLoading,
} from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersSelector';
import { fetchCustomerResponsibilityMembers } from 'gcs-common/slices/administration/customerResponsibilityMembers/customerResponsibilityMembersThunk';
import { getGlobalSearchInput } from 'gcs-common/slices/administration/globalSearch/globalSearchSelectors';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SORT_ORDER } from '../../../../constants/sortConstants';
import agentIcon from '../../../../img/agent-icon.svg';
import deletedIcon from '../../../../img/delete.svg';
import addCompanyIcon from '../../../../img/plus_white.svg';
import teamIcon from '../../../../img/team-icon.svg';
import { showSuccessNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import AddFabButton from '../../../AddFabButton/AddFabButton';
import PaginatedTable from '../../../PaginatedTable/PaginatedTable';
import PopUpModal from '../../../PopUpModal/PopUpModal';
import CustomerResponsibilityMemberCreate from '../CustomerResponsibilityMemberCreate/CustomerResponsibilityMemberCreate';
import CustomerResponsibilityMemberDelete from '../CustomerResponsibilityMemberDelete/CustomerResponsibilityMemberDelete';
import styles from './styles.module.scss';

const columns = [
  {
    Header: 'Kundenummer',
    accessor: 'customerNumber',
  },
  {
    Header: 'Firmenname',
    accessor: 'customerName',
  },
  {
    Header: 'Typ',
    accessor: 'type',
    styles: {
      paddingRight: '0',
      paddingLeft: '0',
    },
    Cell: (cell) => {
      const cellValueCheck = cell.value === CustomerResponsibilityMemberTypes.INDIVIDUAL;
      return (
        <div className={cellValueCheck ? styles.cellDivType : styles.cellDivTypeTeam}>
          <img src={cellValueCheck ? agentIcon : teamIcon} alt={cell.value} />
        </div>
      );
    },
  },
  {
    Header: () => (
      <div className={styles.columnHeaderWithTooltip}>
        Beteiligtenrolle
        <InfoTooltip text={CHIP_TOOLTIP_TEXT.CRM_SYNCHED_FIRST} />
      </div>
    ),
    id: 'roleName',
    accessor: (d) => {
      const cellValueCheck = d.type === CustomerResponsibilityMemberTypes.INDIVIDUAL;
      return <div className={cellValueCheck ? styles.cellDivRole : styles.cellDivRoleTeam}>{d.roleName}</div>;
    },
    styles: {
      paddingLeft: '0',
    },
  },
  {
    Header: 'Verkaufsmitarbeiter/Team',
    accessor: 'teamOrAgent',
  },
  {
    Header: 'Erstellt von',
    accessor: 'createdBy',
  },
  {
    Header: 'Geändert am',
    accessor: 'createdAt',
  },
];

const CustomerResponsibilityMembersTable = ({ filter }) => {
  const filteredColumns = columns.filter((column) => !filter.includes(column.accessor));
  const onCustomerResponsibilityMemberSelected = useCallback(() => {}, []);
  const customerResponsibilityMembersLoading = useSelector(getCustomerResponsibilitiesMembersStateLoading);

  const customerResponsibilityMembers = useSelector(getCustomerResponsibilitiesMembers);
  const customerResponsibilityMembersPagination = useSelector(getCustomerResponsibilitiesMembersPaginationResult);
  const globalSearchInput = useSelector(getGlobalSearchInput);

  const dispatch = useDispatch();

  const [createCustomerResponsibilityMember, setCreateCustomerResponsibilityMember] = useState(false);
  const [deleteCustomerResponsibilityMember, setDeleteCustomerResponsibilityMember] = useState(null);

  const accessorToBeSorted = columns.find((element) => element.Header).accessor;
  const [sortedColumnId, setSortedColumnId] = useState(accessorToBeSorted);
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.ASCENDING);

  const loadCustomerResponsibilityMembers = useCallback(
    (pageIndex, pageSize, searchQuery, searchBy) => {
      dispatch(
        fetchCustomerResponsibilityMembers({
          pageIndex,
          pageSize,
          searchQuery,
          searchBy,
          sortOrder,
          sortedColumnId,
        }),
      );
    },
    [dispatch, sortedColumnId, sortOrder],
  );

  const customerResponsibilityMemberIsCreatedSuccessfully = useCallback(() => {
    setCreateCustomerResponsibilityMember(false);
    dispatch(showSuccessNotification('Erfolgreich hinzugefügt'));
  }, [dispatch]);

  const deleteSelected = useCallback((event, rowData) => {
    event.stopPropagation();
    const { original } = rowData;
    setDeleteCustomerResponsibilityMember(original);
  }, []);

  const customerResponsibilityMemberIsDeletedSuccessfully = useCallback(() => {
    setDeleteCustomerResponsibilityMember(null);
    dispatch(showSuccessNotification('Erfolgreich entfernt'));
  }, [dispatch]);

  return (
    <>
      <PaginatedTable
        data={customerResponsibilityMembers}
        columns={filteredColumns}
        onPaginationChanged={loadCustomerResponsibilityMembers}
        pagination={customerResponsibilityMembersPagination}
        searchInput={globalSearchInput}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        sortedColumnId={sortedColumnId}
        setSortedColumnId={setSortedColumnId}
        sortIsLoading={customerResponsibilityMembersLoading}
        onRowSelected={onCustomerResponsibilityMemberSelected}
        renderActions={({ row }) => (
          <>
            <button type="button" onClick={(event) => deleteSelected(event, row)}>
              <img alt="deleted-icon" src={deletedIcon} />
            </button>
          </>
        )}
      />

      <AddFabButton
        icon={addCompanyIcon}
        title="Verantwortlichkeiten"
        onClick={() => setCreateCustomerResponsibilityMember(true)}
      />
      <PopUpModal
        title="Neue Verantwortlichkeit"
        onRequestClose={() => setCreateCustomerResponsibilityMember(false)}
        isOpen={createCustomerResponsibilityMember}
      >
        <CustomerResponsibilityMemberCreate
          onCancel={() => setCreateCustomerResponsibilityMember(false)}
          onSuccess={customerResponsibilityMemberIsCreatedSuccessfully}
        />
      </PopUpModal>
      {deleteCustomerResponsibilityMember && (
        <PopUpModal
          title="VERANTWORTLICHKEITEN LÖSCHEN"
          onRequestClose={() => setDeleteCustomerResponsibilityMember(null)}
          isOpen={!!deleteCustomerResponsibilityMember}
        >
          <CustomerResponsibilityMemberDelete
            onCancel={() => setDeleteCustomerResponsibilityMember(null)}
            customerResponsibilityMember={deleteCustomerResponsibilityMember}
            onSuccess={customerResponsibilityMemberIsDeletedSuccessfully}
          />
        </PopUpModal>
      )}
    </>
  );
};

CustomerResponsibilityMembersTable.propTypes = {
  filter: PropTypes.arrayOf(PropTypes.string).isRequired,
};
export default CustomerResponsibilityMembersTable;
